export function extractDigits(input: string): string {
  let digits = input.replace(/\D/g, "")
  // Drop a leading US country code so "1XXXXXXXXXX" (11) → "XXXXXXXXXX" (10).
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
  return digits.slice(0, 10)
}

export function formatPhone(input: string): string {
  const digits = extractDigits(input)
  if (digits.length === 0) return ""
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
}

export function toE164(input: string): string {
  return `+1${extractDigits(input)}`
}

/**
 * Vista por defecto de un teléfono en pantallas visibles al público (POS
 * "Órdenes en espera" — Task 12, spec 2026-09-08-kiosko-abandono-orden-guardada):
 * solo los últimos 4 dígitos, para que un cliente parado frente al cajero no
 * pueda leer el número completo sobre el hombro. `formatPhone` sigue siendo
 * la vista completa, para revelar solo al tocar.
 */
export function maskPhone(input: string): string {
  const digits = extractDigits(input)
  if (digits.length === 0) return ""
  return `··· ${digits.slice(-4)}`
}

/** E.164 genérico: `+` seguido de 10–15 dígitos. NO conoce la regla del NANP. */
const E164_ANY = /^\+\d{10,15}$/

/**
 * ¿Este E.164 es **enviable**? Además del formato genérico exige la regla del
 * NANP: un `+1` tiene que traer EXACTAMENTE 10 dígitos nacionales.
 *
 * Por qué existe: `/^\+\d{10,15}$/` a secas deja pasar `+1` + 9 dígitos —
 * son 10 dígitos en total, así que el genérico lo aprueba — y ese número no
 * existe. Twilio lo rechaza con 21211 "Invalid 'To' Phone Number" y GHL
 * tampoco lo entrega, así que el cliente NO recibe ningún SMS: ni recibo, ni
 * "orden lista", ni campaña. La orden se guarda "bien" y el fallo solo se ve
 * después, en el proveedor.
 *
 * Medido en prod: 24 órdenes quedaron así (todas del kiosko, ≤ 2026-08-15,
 * códigos de área 817/469/945/682/492/214 — Dallas/Fort Worth mal tecleados).
 */
export function isSendableE164(phone: string): boolean {
  if (!E164_ANY.test(phone)) return false
  // `+1` + 10 dígitos nacionales = 12 caracteres.
  if (phone.startsWith("+1")) return phone.length === 12
  return true
}

/**
 * `+1` + 10 dígitos, o `null` si el número no está completo.
 *
 * Úsala en TODA ruta que vaya a **guardar** o **enviar**. `toE164` a secas
 * prefija `+1` a lo que le den (`toE164("46955512") === "+146955512"`) y por
 * eso solo sirve cuando quien llama ya verificó los 10 dígitos.
 */
export function toE164OrNull(input: string): string | null {
  const e164 = toE164(input)
  return isSendableE164(e164) ? e164 : null
}
